/**
 * 맥스매치 사용자 상세 — 레거시 `maxMatchInfoDetail.js` 포팅.
 * RDS: PageHeader → FilterCard → DataCard.
 *
 * clinicId/page/pageSize는 URL query에 동기화해 새로고침·공유 시 유지한다.
 * 상세 조회 queryKey에는 객체 대신 clinicId/page/size 원시값만 쓴다.
 */
import {
  useDownloadMaxMatchExcel,
  useMaxMatchUserClinics,
  useMaxMatchUserDetails,
} from '@/entities/max-match/hooks/useMaxMatch';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { FilterRow } from '@/shared/components/ui/row';
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import {
  downloadXlsx,
  MAX_MATCH_EXCEL_HEADER_MAP,
} from '@/shared/utils/xlsxExport';
import { formatDateTime, formatYearMonth } from '@/shared/lib/dateFormat';
import { handleApiError } from '@/shared/utils/toast';
import { useNavigate, useParams, useRouter } from '@tanstack/react-router';
import { Download, Sparkles } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';

const formatCountOrAmount = (value?: number | null): string =>
  value ? value.toLocaleString() : '-';

const DETAIL_SEARCH_DEFAULTS = {
  companyName: '',
  clinicId: '',
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

export const MaxMatchInfoDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const router = useRouter();
  const { userId: userIdParam } = useParams({ strict: false }) as {
    userId?: string;
  };
  const userId = Number(userIdParam ?? 0);

  const { values, setValues } = useListSearchParams(DETAIL_SEARCH_DEFAULTS);

  const clinicId = values.clinicId;
  const page = values.page;
  const pageSize = values.pageSize;
  const pageable = useMemo(
    () => ({ page: page - 1, size: pageSize }),
    [page, pageSize],
  );

  const { data: clinics = [], isLoading: clinicsLoading } =
    useMaxMatchUserClinics(userId);

  const { data, isLoading, isFetching, isError, error } = useMaxMatchUserDetails(
    userId,
    clinicId,
    pageable,
  );
  const downloadExcel = useDownloadMaxMatchExcel();

  useEffect(() => {
    if (isError && error) handleApiError(error);
  }, [isError, error]);

  // 레거시: query clinicId가 clinics에 없으면 전체('')로 보정
  useEffect(() => {
    if (!clinicId || clinicsLoading) return;
    if (clinics.length === 0) return;
    const exists = clinics.some((c) => String(c.clinicId) === String(clinicId));
    if (!exists) {
      setValues({ clinicId: '', page: 1 });
    }
  }, [clinicId, clinics, clinicsLoading, setValues]);

  const rows = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;
  // 레거시: `{companyName || '업체명'} max match 내역`
  const title = `${values.companyName || '업체명'} max match 내역`;

  const handleDownload = async (row: {
    runId: number;
    clinicId: number;
    clinicName: string;
    refYm: string;
    creationType: string;
  }) => {
    try {
      const result = await downloadExcel.mutateAsync({
        userId,
        clinicId: row.clinicId,
        prescriptionYearMonth: row.refYm,
        creationType: row.creationType,
        runId: row.runId,
      });
      downloadXlsx(
        result,
        `${row.refYm}_${row.clinicName}_맥스매치_정보`,
        MAX_MATCH_EXCEL_HEADER_MAP,
      );
    } catch (e) {
      handleApiError(e);
    }
  };

  if (clinicsLoading && !clinics.length) {
    return <LoadingSpinner tip="맥스매치 상세를 불러오는 중..." />;
  }

  return (
    <div className="space-y-5">
      <PageHeader icon={Sparkles} title={title} />

      <FilterCard>
        <FilterRow as="div" gap={12} justify="between">
          <div className="space-y-1">
            <Label>병의원</Label>
            <Select
              value={clinicId || 'all'}
              onValueChange={(v) => {
                setValues({
                  clinicId: v === 'all' ? '' : v,
                  page: 1,
                });
              }}
            >
              <SelectTrigger className="w-[300px]">
                <SelectValue placeholder="전체" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">전체</SelectItem>
                {clinics.map((c) => (
                  <SelectItem key={c.clinicId} value={String(c.clinicId)}>
                    {c.clinicName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            size="sm"
            className="ruda-button"
            onClick={() => {
              // 레거시 router.back() — 검색 조건 유지된 목록으로 복귀
              if (window.history.length > 1) {
                router.history.back();
                return;
              }
              navigate({ to: ROUTE_PATHS.MAX_MATCH as never });
            }}
          >
            나가기
          </Button>
        </FilterRow>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <LoadingSpinner tip="맥스매치 내역을 불러오는 중..." />
        ) : rows.length === 0 ? (
          <EmptyState description="조회된 맥스매치 실행 이력이 없습니다." />
        ) : (
          <>
            <Table className={isFetching ? 'opacity-70' : undefined}>
              <TableHeader>
                <TableRow>
                  <TableHead>일시</TableHead>
                  <TableHead>처방 년 월</TableHead>
                  <TableHead>병의원 명</TableHead>
                  <TableHead>생성 타입</TableHead>
                  <TableHead>유지 제안</TableHead>
                  <TableHead>변경 제안</TableHead>
                  <TableHead>처방 예상 금액</TableHead>
                  <TableHead>max match 예상 금액</TableHead>
                  <TableHead>다운로드</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.runId}>
                    <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell>{formatYearMonth(row.refYm)}</TableCell>
                    <TableCell>{row.clinicName || '-'}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          row.creationType === 'SYSTEM' ? 'default' : 'secondary'
                        }
                      >
                        {row.creationType === 'SYSTEM' ? '시스템' : '사용자'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {formatCountOrAmount(row.unchangedCount)}
                    </TableCell>
                    <TableCell>
                      {formatCountOrAmount(row.changedCount)}
                    </TableCell>
                    <TableCell>
                      {formatCountOrAmount(row.originEstimatedTotalAmount)}
                    </TableCell>
                    <TableCell>
                      {formatCountOrAmount(row.newEstimatedTotalAmount)}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        className="bg-ruda-success text-white hover:bg-ruda-success/90"
                        onClick={() => handleDownload(row)}
                        disabled={downloadExcel.isPending}
                      >
                        <Download className="mr-1 h-3 w-3" />
                        엑셀 다운로드
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              onPageChange={(nextPage) => setValues({ page: nextPage })}
              onPageSizeChange={(nextSize) =>
                setValues({ pageSize: nextSize, page: 1 })
              }
            />
          </>
        )}
      </DataCard>
    </div>
  );
};
